// React
import React, { useRef, useState, useEffect, Fragment } from 'react'
import PropTypes from 'prop-types'

// Components
import BCartPicker from 'ui/blocks/Picker/types/cart'
import BPicker from 'ui/blocks/Picker'
import Icon from 'ui/components/Icon'
import * as Drawer from 'ui/components/Picker/Drawer'
import { FreePicker } from './Picker'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'
import { useFormikContext } from 'formik'
import { useTranslation } from 'shared/utils/useTranslation'

// Shared
import { Format as FormatUtils } from 'back_office/v2/utils/utils'

export const FreeMobile = (props) => {
  const timeoutRef = useRef(null)
  const portalRef = useRef(null)

  const [open, setOpen] = useState(false)

  const picker = usePicker()
  const form = useFormikContext()
  const { t } = useTranslation('user')

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const selectWithTimeout = (selection) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      picker.setSelection(selection)
    }, 750)
  }

  const handleOpen = (e, type) => {
    e.preventDefault()

    setOpen(true)

    selectWithTimeout(type)
  }

  const handleStartChange = (value) => {
    props.onStartChange(value)
  }

  const handleStopChange = (value) => {
    props.onStopChange(value)

    if (!picker.useTimes && !picker.hasInitialValues) {
      return setOpen(false)
    }
  }

  const handleStopTimeChange = () => {
    if (picker.hasInitialValues) return

    setOpen(false)
  }

  const handleDrawerOpenChange = (open) => {
    // Reset selection on drawer close
    if (!open) {
      picker.setSelection(null)
    }

    setOpen(open)
  }

  return (
    <Fragment>
      <BCartPicker.Period disabled={props.disabled} className={picker.useTimes === false ? 'no-times' : ''}>
        <BPicker.Field>
          <BPicker.Label>{t('user.store.start')}</BPicker.Label>
          <BPicker.PickerWrapper data-tid="Starts at picker">
            <BPicker.Button
              onClick={(e) => handleOpen(e, 'from')}
              empty={!form.values.starts_at}
              error={(!!form.errors.starts_at?.date || !!form.errors.starts_at?.time)}
              data-tid="Starts at picker button"
            >
              {form.values.starts_at ?
                FormatUtils.date(form.values.starts_at, picker.useTimes ? 'dateTime' : 'date') :
                t(
                  picker.useTimes ?
                    'user.store.select_date_and_time_placeholder' :
                    'user.store.select_date_placeholder'
                )}
            </BPicker.Button>
          </BPicker.PickerWrapper>
        </BPicker.Field>
        <BPicker.Decorator>
          <Icon icon="arrow-right" />
        </BPicker.Decorator>
        <BPicker.Field>
          <BPicker.Label>{t('user.store.stop')}</BPicker.Label>
          <BPicker.PickerWrapper data-tid="Stops at picker">
            <BPicker.Button
              onClick={(e) => handleOpen(e, 'to')}
              empty={!form.values.stops_at}
              error={(!!form.errors.stops_at?.date || !!form.errors.stops_at?.time)}
              data-tid="Stops at picker button"
            >
              {form.values.stops_at ?
                FormatUtils.date(form.values.stops_at, picker.useTimes ? 'dateTime' : 'date') :
                t(
                  picker.useTimes ?
                    'user.store.select_date_and_time_placeholder' :
                    'user.store.select_date_placeholder'
                )}
            </BPicker.Button>
          </BPicker.PickerWrapper>
        </BPicker.Field>
      </BCartPicker.Period>
      <Drawer.Root open={open} onOpenChange={handleDrawerOpenChange}>
        <Drawer.Header>
          <Drawer.Title>{t('user.store.select_a_period')}</Drawer.Title>
          <FreePicker
            {...props}
            numberOfMonths={1}
            portalRef={portalRef}
            onStartChange={handleStartChange}
            onStopChange={handleStopChange}
            onStopTimeChange={handleStopTimeChange}
            maxHeight="100%"
            width="100%"
            isMobile
            usePortal
          />
        </Drawer.Header>
        <Drawer.Body>
          <BCartPicker.Portal ref={portalRef} />
        </Drawer.Body>
      </Drawer.Root>
    </Fragment>
  )
}

FreeMobile.propTypes = {
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  // Other props
  disable: PropTypes.shape({
    starts_at: PropTypes.bool,
    stops_at: PropTypes.bool,
    start_location_id: PropTypes.bool,
    stop_location_id: PropTypes.bool
  }),
  disabled: PropTypes.bool,
  onStartChange: PropTypes.func,
  onStopChange: PropTypes.func,
  onStartBlur: PropTypes.func,
  onStopBlur: PropTypes.func,
  onMonthChange: PropTypes.func
}
